import { subscribeToLocation } from "@swan-io/chicane";
import { useEffect } from "react";
import type { ErrorState } from "../components/ErrorBoundary";
import { FullScreenScrollView } from "../components/FullScreenScrollView";
import { Link } from "../components/Text";
import { invariantColor } from "../utils/design";
import { t, tNested } from "../utils/i18n";
import { router } from "../utils/routes";
import { IconPage } from "./IconPage";
import { NotFoundPage } from "./NotFoundPage";

type Props = {
  resetError: () => void;
  error: ErrorState;
};

export const ErrorPage: React.FC<Props> = ({ error, resetError }) => {
  const route = router.useRoute(["OrgArea"]);

  useEffect(() => {
    const unsubscribe = subscribeToLocation(resetError);
    return unsubscribe;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const isServerNotFoundError =
    error.type === "SERVER_ERROR" && error.error.errors[0]?.extensions.type === "NOT_FOUND";

  if (isServerNotFoundError) {
    return <NotFoundPage />;
  }

  return (
    <FullScreenScrollView
      paddingHorizontal={24}
      paddingVertical={24}
      maxWidth={478}
      verticalCenter={0.5}
    >
      <IconPage
        variant="negative"
        icon="warning_circle"
        title={t("error_page.title")}
        description={tNested("error_page.message", {
          l: (text) => (
            <Link to="mailto:support@bireme.io" openInNewTab={true} color={invariantColor.white}>
              {text}
            </Link>
          ),
        })}
        submitText={t("error_page.back_to_home")}
        onSubmit={() => {
          route?.name === "OrgArea"
            ? router.push("OrgHome", { org_slug: route.params.org_slug })
            : router.push("Index");
          resetError();
        }}
      />
    </FullScreenScrollView>
  );
};
